import React, { useMemo, useState } from 'react';
import ConfirmModal from '../components/ConfirmModal';
import DocumentCard from '../components/DocumentCard';
import DocumentFormModal from '../components/DocumentFormModal';
import useDocuments from '../hooks/useDocuments';
import useGridColumns from '../hooks/useGridColumns';
import { createDocument, deleteDocument, updateDocument } from '../services/documentsApi';

const DocumentsList = () => {
  const { documents, loading, error, refetch } = useDocuments();
  const [search, setSearch] = useState('');
  const [formModal, setFormModal] = useState({ open: false, mode: 'add', document: null });
  const [deleteModal, setDeleteModal] = useState({ open: false, document: null });
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const gridColumns = useGridColumns();
  const gridStyle = {
    gridTemplateColumns: `repeat(${gridColumns}, minmax(0, 1fr))`,
  };

  const filteredDocuments = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return documents;

    return documents.filter((document) =>
      document.title.toLowerCase().includes(query)
    );
  }, [documents, search]);

  const openAddForm = () => {
    setFormError(null);
    setFormModal({ open: true, mode: 'add', document: null });
  };

  const openEditForm = (document) => {
    setFormError(null);
    setFormModal({ open: true, mode: 'edit', document });
  };

  const closeFormModal = () => {
    if (saving) return;
    setFormModal({ open: false, mode: 'add', document: null });
    setFormError(null);
  };

  const openDeleteModal = (document) => {
    setDeleteError(null);
    setDeleteModal({ open: true, document });
  };

  const closeDeleteModal = () => {
    if (deleting) return;
    setDeleteModal({ open: false, document: null });
    setDeleteError(null);
  };

  const handleSave = async (formData) => {
    try {
      setSaving(true);
      setFormError(null);

      if (formModal.mode === 'edit') {
        await updateDocument(formModal.document.id, formData);
      } else {
        await createDocument(formData);
      }

      await refetch();
      closeFormModal();
    } catch (err) {
      setFormError(err.message || 'Failed to save document');
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteModal.document?.id) return;

    try {
      setDeleting(true);
      setDeleteError(null);
      await deleteDocument(deleteModal.document.id);
      await refetch();
      closeDeleteModal();
    } catch (err) {
      setDeleteError(err.message || 'Failed to delete document');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <>
      <header className="mb-4">
        <div className="flex sm:hidden gap-2 items-center w-full">
          <div className="relative flex-1 min-w-0">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
              aria-label="Search documents"
            />
          </div>
          <button
            type="button"
            onClick={openAddForm}
            className="inline-flex items-center justify-center h-10 w-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg"
            aria-label="Add document"
          >
            <i className="fa-solid fa-plus" />
          </button>
        </div>

        <div className="hidden sm:grid gap-4 w-full" style={gridStyle}>
          <div className="relative min-w-0">
            <i className="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search"
              className="w-full h-10 rounded-lg border border-gray-300 bg-white pl-9 pr-3 text-sm outline-none focus:border-gray-400"
              aria-label="Search documents"
            />
          </div>
          <button
            type="button"
            onClick={openAddForm}
            style={{ gridColumn: gridColumns }}
            className="inline-flex items-center justify-center gap-2 h-10 shrink-0 bg-orange-500 hover:bg-orange-600 text-white rounded-lg px-4 text-sm font-medium justify-self-end"
            aria-label="Add document"
          >
            <i className="fa-solid fa-plus" />
            Add document
          </button>
        </div>
      </header>

      {loading && (
        <p className="text-center text-gray-600 py-8">Loading documents...</p>
      )}

      {error && (
        <p className="text-center text-red-600 py-8">{error}</p>
      )}

      {!loading && !error && filteredDocuments.length === 0 && (
        <p className="text-center text-gray-600 py-8">No documents found.</p>
      )}

      {!loading && !error && filteredDocuments.length > 0 && (
        <div className="grid gap-4 w-full" style={gridStyle}>
          {filteredDocuments.map((document) => (
            <DocumentCard
              key={document.id}
              document={document}
              onEdit={openEditForm}
              onDelete={openDeleteModal}
            />
          ))}
        </div>
      )}

      <DocumentFormModal
        isOpen={formModal.open}
        mode={formModal.mode}
        initialValues={formModal.document}
        onClose={closeFormModal}
        onSave={handleSave}
        saving={saving}
        error={formError}
      />

      <ConfirmModal
        isOpen={deleteModal.open}
        title="Delete document"
        message={`Are you sure you want to delete "${deleteModal.document?.title}"? This action cannot be undone.`}
        error={deleteError}
        confirmLabel="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={closeDeleteModal}
        variant="danger"
        loading={deleting}
      />
    </>
  );
};

export default DocumentsList;
